import { useCallback, useEffect, useState } from 'react'

/**
 * Uma lista vinda de uma porta, com os três estados que `use-members.ts` já
 * estabeleceu — e pelo mesmo motivo: **lista vazia e lista não carregada dizem
 * coisas diferentes na tela**, e um array só não distingue as duas. Uma casa
 * sem cartão nenhum e uma leitura que falhou não podem produzir a mesma tela.
 *
 * O que este acrescenta ao de lá é o `reload`, que a Task 11 precisa e a Fase 1
 * não precisava: aqui a tela **escreve**, e depois de escrever ela tem de
 * reler. Sem isso a categoria recém-criada não apareceria até um F5 — e pior,
 * o `path` que a Cloud Function cunha só existe **depois** da escrita do
 * cliente, então a primeira leitura de uma categoria nova legitimamente mostra
 * "sem caminho". Reler é o que faz o defeito temporário desaparecer sozinho.
 */
export type CollectionState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; items: T[] }
  | { status: 'failed' }

export type Collection<T> = {
  state: CollectionState<T>
  /** Relê. Chamado depois de toda escrita. */
  reload: () => void
}

/**
 * `load` precisa ser estável (`useCallback` no chamador): é ele que dispara o
 * efeito, e uma função nova a cada render viraria um laço de leitura.
 */
export function useCollection<T>(load: () => Promise<T[]>): Collection<T> {
  const [state, setState] = useState<CollectionState<T>>({ status: 'loading' })
  const [version, setVersion] = useState(0)

  useEffect(() => {
    // A resposta pode chegar depois do desmonte — sair da tela enquanto a
    // leitura está no ar é o caminho comum, não a exceção.
    let live = true

    void load()
      .then((items) => {
        if (live) setState({ status: 'loaded', items })
      })
      .catch((error: unknown) => {
        // Falha vira estado visível **e** fica no console. Engolir produziria a
        // lista vazia que a tela leria como "ainda não há nada".
        console.error('useCollection: a leitura falhou', error)
        if (live) setState({ status: 'failed' })
      })

    return () => {
      live = false
    }
  }, [load, version])

  const reload = useCallback(() => {
    setVersion((current) => current + 1)
  }, [])

  return { state, reload }
}
